/**
 * Authoring one question — the fields, once, for the two surfaces that ask for them.
 *
 * The editor's „Nowe pytanie" card and the shelf's own dialog collect the SAME thing, and
 * `CustomQuestionRules.Validate` refuses exactly the same things for both: that is the backend's
 * stated reason for having one entry point, and it is this file's. A second copy of the 4 × 2 type
 * grid is the near-duplicate the styling rules exist to stop.
 *
 * One difference between the callers is real, so it is a parameter: **the recording type**. A survey
 * may ask for one (`allowClip`), the shelf may not — a question naming one specific movement is not
 * reusable, and `CustomQuestionRules` refuses to save one.
 *
 * „Jednostka" was the second, and it is GONE (17 September 2026). The shelf's column stores a unit
 * and nothing on this side can ever read it back: `SurveyTemplateItemInput` carries no unit, so the
 * copy into a survey drops it, and `/raporty/katalog` never prints one. A trainer typed „kg" and it
 * vanished. The contract gap is recorded in `AGENTS.md`; the day a template item can carry a unit,
 * the field comes back with somewhere to go.
 *
 * „Pytanie o zdrowie" is drawn by neither. `isHealthData` does not survive the copy into a template
 * and the consent gate keys on the standard catalog's own codes, so the chip would promise a
 * protection that does not exist.
 */

import { useId } from 'react';

import { LockIcon } from '~/shared/ui/AuthIcons';
import inlineText from '~/shared/ui/inlineTextButton.module.css';

import {
  AUTHORABLE_ANSWER_TYPES,
  CHOICE_ANSWER_TYPES,
  MAX_CHOICE_OPTIONS,
  MIN_CHOICE_OPTIONS,
} from '../model/editorDraft';
import type { QuestionFieldsValue } from '../model/questionFields';
import type { TranslateKey } from '../model/translateKey';
import { AnswerPreview } from './AnswerPreview';
import { answerTypeIcon } from './AnswerTypePill';
import { TrashIcon } from '~/shared/ui/ReportIcons';
import canon from '~/shared/ui/surveyCanon.module.css';
import styles from './EditorForm.module.css';

export interface QuestionFieldsProps {
  readonly value: QuestionFieldsValue;
  readonly onChange: (value: QuestionFieldsValue) => void;
  /** The recording type is offered only where a survey can hold one. */
  readonly allowClip: boolean;
  /** How many recordings this survey still has room for. Ignored when `allowClip` is false. */
  readonly clipsLeft: number;
  readonly t: TranslateKey;
}

export function QuestionFields({
  value,
  onChange,
  allowClip,
  clipsLeft,
  t,
}: QuestionFieldsProps) {
  const labelId = useId();
  const typeLabelId = useId();
  const optionsLabelId = useId();

  const isChoice = CHOICE_ANSWER_TYPES.includes(value.answerType);
  const types = AUTHORABLE_ANSWER_TYPES.filter(
    (type) => allowClip || type !== 'ExerciseClip',
  );

  return (
    <>
      <div className={styles.field}>
        <label className={styles.label} htmlFor={labelId}>
          {t('web:reports.editor.newQuestion.label')}
        </label>
        <input
          id={labelId}
          className={styles.textField}
          value={value.label}
          onChange={(event) => {
            onChange({ ...value, label: event.target.value });
          }}
        />
      </div>

      <div className={styles.field}>
        <span className={styles.label} id={typeLabelId}>
          {t('web:reports.editor.newQuestion.type')}
        </span>
        <div className={styles.typeGrid} role="radiogroup" aria-labelledby={typeLabelId}>
          {types.map((type) => {
            // The cap is stated on the chip rather than removing it: a type that vanished would
            // leave the trainer wondering whether they had imagined it.
            const refusal =
              type === 'ExerciseClip' && clipsLeft === 0
                ? t('web:reports.editor.newQuestion.clipCapReached')
                : null;
            const checked = value.answerType === type;
            return (
              <button
                key={type}
                type="button"
                role="radio"
                aria-checked={checked}
                aria-disabled={refusal !== null ? true : undefined}
                title={refusal ?? t(`intakeSurvey.answerType.${type}`)}
                tabIndex={checked ? 0 : -1}
                className={styles.typeChip}
                onClick={() => {
                  if (refusal !== null) return;
                  onChange({ ...value, answerType: type });
                }}
              >
                {answerTypeIcon(type, styles.typePillIcon)}
                <span className={styles.typeChipLabel}>{t(`intakeSurvey.answerType.${type}`)}</span>
              </button>
            );
          })}
        </div>
      </div>

      {isChoice ? (
        <div className={styles.field}>
          <span className={styles.label} id={optionsLabelId}>
            {t('web:reports.editor.newQuestion.options', {
              min: MIN_CHOICE_OPTIONS,
              max: MAX_CHOICE_OPTIONS,
            })}
          </span>
          <div className={styles.options} role="group" aria-labelledby={optionsLabelId}>
            {value.options.map((option, index) => (
              <div key={index} className={styles.optionRow}>
                <span className={styles.optionIndex} aria-hidden>
                  {index + 1}
                </span>
                <input
                  className={styles.textField}
                  value={option}
                  aria-label={t('web:reports.editor.newQuestion.option', { index: index + 1 })}
                  onChange={(event) => {
                    const next = [...value.options];
                    next[index] = event.target.value;
                    onChange({ ...value, options: next });
                  }}
                />
                <button
                  type="button"
                  className={`${String(canon.ibtn)} ${String(canon.ibtnBare)} ${String(canon.ibtnDanger)}`}
                  aria-label={t('web:reports.editor.newQuestion.removeOption', { index: index + 1 })}
                  aria-disabled={value.options.length <= MIN_CHOICE_OPTIONS ? true : undefined}
                  title={
                    value.options.length <= MIN_CHOICE_OPTIONS
                      ? t('web:reports.editor.newQuestion.optionFloor', { min: MIN_CHOICE_OPTIONS })
                      : undefined
                  }
                  onClick={() => {
                    if (value.options.length <= MIN_CHOICE_OPTIONS) return;
                    onChange({ ...value, options: value.options.filter((_, at) => at !== index) });
                  }}
                >
                  <TrashIcon className={canon.ibtnIconMd} />
                </button>
              </div>
            ))}
            {value.options.length < MAX_CHOICE_OPTIONS ? (
              <button
                type="button"
                className={inlineText.inlineTextButton}
                onClick={() => {
                  onChange({ ...value, options: [...value.options, ''] });
                }}
              >
                {t('web:reports.editor.newQuestion.addOption')}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      <AnswerPreview
        answerType={value.answerType}
        options={value.options}
        clipsLeft={clipsLeft}
        t={t}
      />

      <div className={styles.twoUp}>
        <div className={styles.field}>
          <span className={styles.label}>{t('web:reports.editor.newQuestion.settings')}</span>
          <button
            type="button"
            className={styles.toggleChip}
            aria-pressed={value.required}
            // A recording is always skippable — the server discards the flag for that type — so the
            // chip states it rather than offering a setting that would be thrown away.
            aria-disabled={value.answerType === 'ExerciseClip' ? true : undefined}
            title={
              value.answerType === 'ExerciseClip'
                ? t('web:reports.editor.newQuestion.clipAlwaysOptional')
                : undefined
            }
            onClick={() => {
              if (value.answerType === 'ExerciseClip') return;
              onChange({ ...value, required: !value.required });
            }}
          >
            <LockIcon className={styles.toggleIcon} />
            {t('web:reports.editor.newQuestion.required')}
          </button>
        </div>
      </div>

      <p className={styles.newNote}>{t('web:reports.editor.newQuestion.requiredHint')}</p>
    </>
  );
}
